import React from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import { UserContext, INITIAL_USER_CONTEXT_VALUE, lightTheme, darkTheme } from "./userStore"
import { observeAuthState, auth } from "../../firebaseAuth"
import { User, signOut } from "firebase/auth"
import {
  DocumentData,
  Query,
  Timestamp,
  and,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore"
import { DateTime } from "luxon"
import { db as firestore } from "../../firebaseFirestore"
import i18n from "../../i18n"
import { LocationType, getLocationQuery } from "../../utils/locationQuery"
import { readUrlContext, resolveStoredLanguageCode } from "../../utils/urlContext"
import { ensureUserProfileDocument, isRegisteredAuthUser } from "../../services/accountProfileService"

import { ISponsor } from "./Sponsor"

// import { OffersConfig } from "./OffersConfig"

import { LoadingService } from "react-ui-components/components/LoadingService"
import { PopupService } from "react-ui-components/components/PopupService"
import { useAuthState } from "react-firebase-hooks/auth"
import { overlaySponsorAssignments, resolveSponsorAssignments } from "../../contentAssignments"
import { getBusinessSlugLookupValues } from "../../utils/sponsorSlug"
import { createLatestRequestGuard, type RequestGuard } from "../../utils/latestRequestGuard"
import { useErrorHandler } from "../../hooks/useErrorHandler"

let userRolesCache: any = null
const THEME_MODE_STORAGE_KEY = "wf-theme-mode"
const USER_TICKETS_ACTIVE_REFRESH_MS = 60_000
// Sponsors previously listened to the entire matching collection with no `limit()` at all (see
// getSponsorRef below). This bounds the initial read and grows on loadMoreSponsors() instead,
// mirroring the raffles pagination pattern without changing listenForSponsorsChanges' call
// signature (it's threaded through several screens and test overrides as a plain
// subscribe-and-return-unsubscribe function).
const SPONSOR_PAGE_SIZE = 10

export const isActiveUserTicket = (ticket: any, now = Date.now()) => {
  if (!ticket || ticket.disabled) {
    return false
  }

  return typeof ticket.expirationDate !== "number" || ticket.expirationDate >= now
}

// The Firestore `users/{uid}.language` field (set at registration) was never applied to
// i18next after login — the app only ever picked a language from the `?lang=` URL param or
// the browser locale (see App.tsx's mount effect), so a signed-in user's stored preference was
// silently ignored and the client app only ever displayed in whatever the browser defaulted to
// (effectively always English for English-locale browsers). An explicit `?lang=` always wins,
// since that's a deliberate link-level override (e.g. a shared referral/QR link).
export const applyStoredUserLanguage = (language: string | undefined | null): void => {
  if (typeof window === "undefined") {
    return
  }
  if (readUrlContext(window.location.search).hasLanguage) {
    return
  }
  const code = resolveStoredLanguageCode(language)
  if (!code) {
    return
  }
  const currentCode = i18n.language ? i18n.language.split("-")[0] : ""
  if (code !== currentCode) {
    i18n.changeLanguage(code)
  }
}

function getInitialModeFromStorage() {
  if (typeof window === "undefined") {
    return INITIAL_USER_CONTEXT_VALUE.mode
  }

  const storedMode = window.localStorage.getItem(THEME_MODE_STORAGE_KEY)
  return storedMode === "dark" || storedMode === "light"
    ? storedMode
    : INITIAL_USER_CONTEXT_VALUE.mode
}

export interface IDisplayContentConfig {
  addedPointsAnimation: string
  availableLocations: any[]
  copyrightMessage: string
  dateTimeFormat: string
  defaultProfileUrl: string
  id: string
  imageResizeMode: string
  pointAnimationLink: string
  qrCodeIcon: string
  raffleTypes: any[]
  rafflesImageHeight: number
  rafflesScreen: {
    descriptionLength: number
    hideAllActions: boolean
    imageResizeMode: string
    rafflesImageHeight: number
  }
  rafflesSectionImageHeight: number
  sponsorsTitle: string
  sponsorsTitleKey: string
  termIcon: string
  vendorPercentage: string
  videoPlayback?: {
    awayBehavior?: "floating" | "stopOnAway"
    floatingPopupEnabled?: boolean
    heartbeatSeconds?: number
    raffleVideo?: {
      fullscreenEnabled?: boolean
    }
    viewThresholdSeconds?: number
  }
}

export const AppProvider: React.FC<{ children: any }> = ({ children }) => {
  const initialMode = getInitialModeFromStorage()
  const [mode, setMode] = useState(initialMode)
  const [theme, setTheme] = useState(initialMode === "light" ? lightTheme : darkTheme)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userInfo, setUserInfo] = useState<any>(null)
  const [ticketsCount, setTicketsCount] = useState(0)
  const [sponsors, setSponsors] = useState<ISponsor[]>([])
  const [isEmailProvider, setIsEmailProvider] = useState(false)
  const [isPhoneProvider, setIsPhoneProvider] = useState(false)
  const [supportInfo, setSupportInfo] = useState({})
  const [displayContentConfig, setDisplayContentConfig] = useState<Partial<IDisplayContentConfig>>({})
  const [loading, setLoading] = useState(false)
  const [usersCount, setUsersCount] = useState()
  const [user] = useAuthState(auth)
  const [userRoles, setUserRoles] = useState<any[]>([])
  const [userIsAdvanced, setUserIsAdvanced] = useState(false)
  const [usertickets, setUsertickets] = useState<any[]>([])
  const [userPoints, setUserPoints] = useState(0)
  const [currentSponsor, setCurrentSponsor] = useState<any>(null)
  const [selectedStoreType, setSelectedStoreType] = useState<any>(null)
  const [sponsorsLimit, setSponsorsLimit] = useState(SPONSOR_PAGE_SIZE)
  const [hasMoreSponsors, setHasMoreSponsors] = useState(true)
  const [isFetchingMoreSponsors, setIsFetchingMoreSponsors] = useState(false)
  const sponsorQuerySignatureRef = useRef<string | null>(null)
  const ticketsUnsubscribeRef = useRef<(() => void) | null>(null)
  const authRequestGuardRef = useRef(createLatestRequestGuard())
  const { handleError } = useErrorHandler("AppProvider")

  useEffect(() => {
    setTheme(mode === "light" ? lightTheme : darkTheme)
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_MODE_STORAGE_KEY, mode)
    }
  }, [mode])

  const updateUserTickets = useCallback((id: string, raffleId?: string) => {
    const result = userTicketsQuery(id, raffleId)
    let latestTickets: any[] = []
    const publishActiveTickets = () => {
      const activeTickets = latestTickets.filter((ticket) => isActiveUserTicket(ticket))
      setUsertickets(activeTickets)
      setTicketsCount(activeTickets.length)
    }
    const refreshTimer = window.setInterval(publishActiveTickets, USER_TICKETS_ACTIVE_REFRESH_MS)
    const unsubscribe = onSnapshot(result, {
      next: (snapshot) => {
        latestTickets = snapshot.docs.map((x) => x.data())
        publishActiveTickets()
      },
      error: (error) => handleError(error, { operation: "updateUserTickets" }),
    })
    return () => {
      window.clearInterval(refreshTimer)
      unsubscribe()
    }
  }, [handleError])

  const resetSponsor = useCallback(() => {
    setSponsors([])
  }, [])

  const loadMoreSponsors = useCallback(() => {
    setIsFetchingMoreSponsors(true)
    setSponsorsLimit((current) => current + SPONSOR_PAGE_SIZE)
  }, [])

  const addSponsor = useCallback(
    (data: ISponsor, reset: boolean = false) => {
      if (data) {
        if (reset || !sponsors) {
          resetSponsor()
        }
        setSponsors((arr) => [data, ...arr])
      }
    },
    [sponsors, resetSponsor],
  )

  const getSelectedStore = useCallback(async (id: string) => {
    if (id) {
      const fbSnap = await getDoc(doc(firestore, "featured-businesses", id))
      const data = fbSnap.data()
      setSelectedStoreType(data)
      return data
    }
    throw new Error("id not defined")
  }, [])

  const getSponsorDoc = useCallback((id?: string) => {
    const ref = collection(firestore, "sponsors")
    return id ? doc(ref, id) : doc(ref)
  }, [])

  const getSponsorByPublicSlug = useCallback(async (slug: string) => {
    const lookupValues = getBusinessSlugLookupValues(slug)
    if (!lookupValues.length) {
      return null
    }

    const sponsorRef = collection(firestore, "sponsors")
    for (const lookupValue of lookupValues) {
      const [businessSlugSnapshot, publicSlugSnapshot] = await Promise.all([
        getDocs(query(sponsorRef, where("businessSlug", "==", lookupValue), limit(1))),
        getDocs(query(sponsorRef, where("publicSlug", "==", lookupValue), limit(1))),
      ])
      const sponsorDoc = businessSlugSnapshot.docs[0] || publicSlugSnapshot.docs[0]
      if (sponsorDoc) {
        return {
          id: sponsorDoc.id,
          ...(sponsorDoc.data() || {}),
        } as ISponsor
      }
    }

    return null
  }, [])

  const listenForSponsorsChanges = useCallback(
    (
      location: LocationType,
      get?: Function | string,
      funcRpc?: Function,
      selectedStoreType?: any,
    ) => {
      console.log("listenForSponsorsChanges--->", location, selectedStoreType)
      let isActive = true
      let requestVersion = 0
      let sponsorId = ""
      let query: any = null
      let filter: string[] | null = null
      let effectiveLimit = sponsorsLimit
      if (typeof get === "string") {
        sponsorId = get
        get = funcRpc
        query = getSponsorDoc(sponsorId)
      } else {
        // Reset pagination only when the listened-to target actually changes (location/store
        // type), not when this call is itself the result of loadMoreSponsors() growing the
        // limit and re-triggering this same callback via the consumer's effect dependency.
        const signature = JSON.stringify({ location, selectedStoreType })
        const isNewTarget = sponsorQuerySignatureRef.current !== signature
        sponsorQuerySignatureRef.current = signature
        if (isNewTarget) {
          effectiveLimit = SPONSOR_PAGE_SIZE
          setSponsorsLimit(SPONSOR_PAGE_SIZE)
          setHasMoreSponsors(true)
        }

        const { query: q, filterId: f } = getSponsorRef(location, selectedStoreType, effectiveLimit)
        query = q
        filter = f
      }

      let isCollection = !!selectedStoreType || !sponsorId
      setCurrentSponsor(null)

      const unsubscribe = onSnapshot(query, {
        next: async (snapshot: any) => {
          const currentRequestVersion = ++requestVersion
          resetSponsor()
          if (!isCollection) {
            const exists =
              typeof snapshot?.exists === "function"
                ? snapshot.exists()
                : !!snapshot?.data?.()
            if (!exists) {
              const sponsorBySlug = sponsorId ? await getSponsorByPublicSlug(sponsorId) : null
              if (!isActive || currentRequestVersion !== requestVersion) {
                return
              }
              if (!sponsorBySlug) {
                setCurrentSponsor(undefined)
                return
              }

              const resolvedAssignments = await resolveSponsorAssignments([sponsorBySlug.id])
              if (!isActive || currentRequestVersion !== requestVersion) {
                return
              }
              setCurrentSponsor(
                overlaySponsorAssignments(
                  sponsorBySlug,
                  resolvedAssignments[sponsorBySlug.id],
                ),
              )
            } else {
              const sponsor = {
                id: snapshot.id,
                ...(snapshot.data() || {}),
              } as ISponsor
              const resolvedAssignments = await resolveSponsorAssignments([sponsor.id])
              if (!isActive || currentRequestVersion !== requestVersion) {
                return
              }
              setCurrentSponsor(
                overlaySponsorAssignments(sponsor, resolvedAssignments[sponsor.id]),
              )
            }
          } else {
            if (!isActive || currentRequestVersion !== requestVersion) {
              return
            }
            setCurrentSponsor(null)
          }
          if (get) {
            get(snapshot)
          } else if (isCollection) {
            const sponsorRows: ISponsor[] = []
            snapshot.forEach((item: any) => {
              const resultData: ISponsor = {
                id: item.id,
                ...(item.data() || {}),
              }
              if (filter?.length && !filter?.includes(item.id)) {
                return
              }
              resultData.image = resultData.image || ""
              sponsorRows.push(resultData)
            })

            const resolvedAssignments = await resolveSponsorAssignments(
              sponsorRows.map((item) => item.id).filter(Boolean),
            )
            if (!isActive || currentRequestVersion !== requestVersion) {
              return
            }

            const hydratedSponsors = sponsorRows
              .map((sponsor) =>
                overlaySponsorAssignments(sponsor, resolvedAssignments[sponsor.id]),
              )
              .reverse()

            setSponsors(hydratedSponsors)
            // snapshot.size (raw docs before the businessIds filter above) reflects whether the
            // bounded query hit its limit, i.e. whether more pages likely exist server-side.
            setHasMoreSponsors(snapshot.size === effectiveLimit)
            setIsFetchingMoreSponsors(false)
          }
        },
        error: (error) => {
          resetSponsor()
          setIsFetchingMoreSponsors(false)
          handleError(error, { operation: "listenForSponsorsChanges" })
        },
      })

      return () => {
        isActive = false
        unsubscribe()
      }
    },
    [getSponsorByPublicSlug, getSponsorDoc, resetSponsor, sponsorsLimit, handleError],
  )

  const setPoints = useCallback((points: number) => {
    const normalizedPoints = Number(points)
    setUserPoints(Number.isFinite(normalizedPoints) && normalizedPoints >= 0 ? normalizedPoints : 0)
  }, [])

  const getUsersStats = useCallback(async (isCurrent: RequestGuard = () => true) => {
    const querySnapshot = doc(firestore, "app-configurations/usersStats")

    const ref = await getDoc(querySnapshot)
    if (!isCurrent()) {
      return
    }
    const data: any = ref.data()
    // usersCountIcon = data?.icon || {}
    setUsersCount(data?.count || 0)
    // console.log("USERS STATS ----->", data)
    return data
  }, [])

  const getUsersTickets = useCallback(
    async (uid?: string) => {
      const id = uid || user?.uid

      if (!id) {
        return 0
      }

      try {
        const querySnapshot = collection(firestore, "users", id, "tickets")

        const result = await getCountFromServer(
          query(
            querySnapshot,
            and(
              where("expirationDate", ">=", Timestamp.now().toMillis()),
              where("disabled", "==", false),
            ),
          ),
        )

        return result?.data()?.count || 0
      } catch (error) {
        // Error fetching ticket count - return 0
        return 0
      }
    },
    [user?.uid],
  )

  const loadUserRoles = useCallback(
    async (uid?: string, isCurrent: RequestGuard = () => true) => {
      uid = uid || user?.uid
      if (!uid || !isCurrent()) {
        return
      }
      if (!userRolesCache) {
        userRolesCache = {}
      }

      if (userRolesCache[uid]) {
        const cachedRoles = userRolesCache[uid]
        setUserRoles(cachedRoles)
        setUserIsAdvanced(!!cachedRoles.find((x: any) => /admin|super|vendor/i.test(x.name)))
      }
      const userRolesDoc = await getDocs(collection(firestore, `users/${uid}/roles`))
      if (!isCurrent()) {
        return
      }

      userRolesCache[uid] = userRolesDoc.docs.map((x) => x.data() as any)
      const roles = userRolesCache[uid]
      setUserRoles(roles)
      setUserIsAdvanced(!!roles.find((x: any) => /admin|super|vendor/i.test(x.name)))
    },
    [user?.uid],
  )

  const loadUser = useCallback(
    async (uid?: string, isCurrent: RequestGuard = () => true) => {
      try {
        uid = uid || auth?.currentUser?.uid
        if (!uid || !isCurrent()) {
          return
        }
        setLoading(true)
        const userDoc = await getDoc(doc(firestore, "users", uid))
        if (!isCurrent()) {
          return
        }
        const data = userDoc.data()
        const loadedUser = data as any
        setUserInfo(loadedUser)
        setPoints(loadedUser?.points || 0)
        applyStoredUserLanguage(loadedUser?.language)
      } catch {
      } finally {
        if (isCurrent()) {
          setLoading(false)
        }
      }
    },
    [setPoints],
  )

  const updateUserContextInfo = useCallback(
    async (user: User | null, isCurrent: RequestGuard = () => true) => {
      let updateUserTicketsUnsubscribe: any = null
      try {
        if (!isCurrent()) {
          return
        }
        userRolesCache = null
        setLoading(true)

        if (user) {
          /*
          "password" – Email & Password authentication
          "phone" – Phone number authentication
          "google.com" – Google Sign-In
          "facebook.com" – Facebook Login
          "twitter.com" – Twitter Login
          "github.com" – GitHub Login
          "microsoft.com" – Microsoft Login (OAuth)
          "apple.com" – Apple Sign-In
          "yahoo.com" – Yahoo Login
          "linkedin.com" – LinkedIn Login (via OAuth)
          "saml.<provider_id>" – SAML identity provider
          "oidc.<provider_id>" – OpenID Connect (OIDC) identity provider
          "anonymous" – Anonymous authentication
          "custom" – Custom authentication via Firebase Admin SDK
          */
          const isEmailProvider = user.providerData.some(
            (provider: { providerId: string }) => provider.providerId === "password",
          )
          setIsEmailProvider(isEmailProvider)

          const isPhoneProvider = user.providerData.some(
            (provider: { providerId: string }) => provider.providerId === "phone",
          )
          setIsPhoneProvider(isPhoneProvider)

          const isLoggedIn = isRegisteredAuthUser(user)

          setIsLoggedIn(isLoggedIn)

          if (isLoggedIn) {
            try {
              await ensureUserProfileDocument({ firestore, authUser: user })
            } catch {
              // Best-effort profile sync; do not block loading user roles, balance, or tickets.
            }
            if (!isCurrent()) {
              return
            }
            await loadUserRoles(user.uid, isCurrent)
            if (!isCurrent()) {
              return
            }
            await loadUser(user.uid, isCurrent)
            if (!isCurrent()) {
              return
            }

            const ticketsCount = await getUsersTickets(user.uid)
            if (!isCurrent()) {
              return
            }
            setTicketsCount(ticketsCount)
            updateUserTicketsUnsubscribe = updateUserTickets(user.uid)
            if (!isCurrent()) {
              updateUserTicketsUnsubscribe()
              updateUserTicketsUnsubscribe = null
              return
            }
            ticketsUnsubscribeRef.current = updateUserTicketsUnsubscribe
          } else {
            setUserInfo(null)
            setUserRoles([])
            setUserIsAdvanced(false)
            setTicketsCount(0)
            setUsertickets([])
            setPoints(0)
          }

          if (!isCurrent()) {
            return
          }
          await getUsersStats(isCurrent)
          if (!isCurrent()) {
            return
          }
          const supportInfo = await getSupportInfo()
          if (!isCurrent()) {
            return
          }
          setSupportInfo(supportInfo)

          const displayContentConfig = await getDisplayContentConfig()
          if (!isCurrent()) {
            return
          }
          setDisplayContentConfig(displayContentConfig || {})
        } else {
          setUserInfo(null)
          setIsLoggedIn(false)
          setIsEmailProvider(false)
          setIsPhoneProvider(false)
          setUserRoles([])
          setUserIsAdvanced(false)
          setTicketsCount(0)
          setUsertickets([])
          setPoints(0)
          setLoading(false)
        }
      } catch {
      } finally {
        if (isCurrent()) {
          setLoading(false)
        }
      }
      return updateUserTicketsUnsubscribe
        ? () => {
            updateUserTicketsUnsubscribe()
            if (ticketsUnsubscribeRef.current === updateUserTicketsUnsubscribe) {
              ticketsUnsubscribeRef.current = null
            }
          }
        : undefined
    },
    [getUsersStats, getUsersTickets, loadUser, loadUserRoles, setPoints, updateUserTickets],
  )

  useEffect(() => {
    let updateUserContextInfoUnsubscribe: any = null
    let previousUid: string | null = null
    const authRequestGuard = authRequestGuardRef.current

    const unsubscribeAuth = observeAuthState(async (user: User | null) => {
      const currentUid = user ? user.uid : null
      if (currentUid === previousUid) {
        return
      }
      previousUid = currentUid
      const isCurrent = authRequestGuard.begin()

      if (updateUserContextInfoUnsubscribe) {
        updateUserContextInfoUnsubscribe()
        updateUserContextInfoUnsubscribe = null
      }
      if (ticketsUnsubscribeRef.current) {
        ticketsUnsubscribeRef.current()
        ticketsUnsubscribeRef.current = null
      }
      const unsubscribe = await updateUserContextInfo(user, isCurrent)
      if (!isCurrent()) {
        unsubscribe?.()
        return
      }
      updateUserContextInfoUnsubscribe = unsubscribe
    })
    return () => {
      authRequestGuard.invalidate()
      updateUserContextInfoUnsubscribe && updateUserContextInfoUnsubscribe()
      if (ticketsUnsubscribeRef.current) {
        ticketsUnsubscribeRef.current()
        ticketsUnsubscribeRef.current = null
      }
      unsubscribeAuth && unsubscribeAuth()
    }
  }, [updateUserContextInfo])

  const setUserData = useCallback(
    async (updatedUser: any) => {
      const user = auth.currentUser
      if (user) {
        await setDoc(doc(firestore, `users/${user.uid}`), updatedUser, {
          merge: true,
        })
        setUserInfo({ ...(userInfo || {}), ...updatedUser })
      }
    },
    [userInfo],
  )

  const updateUser = useCallback(
    async (updatedUser: any) => {
      const user = auth.currentUser
      if (user) {
        await updateDoc(doc(firestore, `users/${user.uid}`), updatedUser)
        setUserInfo({ ...(userInfo || {}), ...updatedUser })
      }
    },
    [userInfo],
  )

  const deleteUser = useCallback(async () => {
    const user = auth.currentUser
    if (user) {
      await user.delete()
      if (user.uid) {
        await deleteDoc(doc(firestore, `users/${user.uid}`))
      }
      setUserInfo(null)
    }
  }, [])

  const handleLogout = useCallback(async () => {
    LoadingService.setLoading(true)
    try {
      if (ticketsUnsubscribeRef.current) {
        ticketsUnsubscribeRef.current()
        ticketsUnsubscribeRef.current = null
      }
      await signOut(auth)
      // Drop everything tied to the closed session right away rather than waiting for the
      // auth observer, so no public component keeps rendering the old user's data.
      userRolesCache = null
      setUserInfo(null)
      setIsLoggedIn(false)
      setIsEmailProvider(false)
      setIsPhoneProvider(false)
      setUserRoles([])
      setUserIsAdvanced(false)
      setTicketsCount(0)
      setUsertickets([])
      setPoints(0)
    } catch (error: any) {
      PopupService.pushPopup(error?.message || error, "Error")
      throw error
    } finally {
      LoadingService.setLoading(false)
    }
  }, [setPoints])

  const getThisMonthOffers = useCallback(async (sponsorId: string): Promise<any> => {
    try {
      const currentMonth = DateTime.now().toFormat("LLyyyy")
      // console.log('getThisMonthOffers ----currentMonth------->', currentMonth)
      // console.log('getThisMonthOffers ----date------->', date)
      // console.log('getThisMonthOffers ----sponsorId------->', sponsorId)
      // console.log('currentMonth---->', currentMonth);
      const ref = query(
        collection(firestore, "sponsors", sponsorId, "offers", currentMonth, "offers"),
        orderBy("order"),
        where("disabled", "==", false),
      )
      ///sponsors/8IAQdwz85Qokfmifsd02/offers/042024/offers
      // const found = self.sponsors.find(x => x.id === id);
      // const data = result.data()
      const arr: any[] = []
      const docsRef = await getDocs(ref)
      docsRef.forEach((element) => {
        const data = element.data()
        // console.log("DATA---->", data)
        // `id`/`sponsorId`/`month` weren't included before, so a per-offer like button had no
        // way to address the right Firestore doc (Trello #211).
        arr.push({ ...data, id: element.id, sponsorId, month: currentMonth })
      })
      return arr
    } catch (error) {
      console.log("There was an error trying to load sponsors offers", error)
    }
  }, [])

  return (
    <UserContext.Provider
      value={{
        ...INITIAL_USER_CONTEXT_VALUE,
        theme,
        mode,
        setMode,
        user: (user || null) as any,
        userInfo,
        isLoggedIn,
        loadUser,
        updateUser,
        deleteUser,
        setPoints,
        addSponsor,
        listenForSponsorsChanges,
        hasMoreSponsors,
        isFetchingMoreSponsors,
        loadMoreSponsors,
        resetSponsor,
        getThisMonthOffers,
        handleLogout,
        setUserData,
        getUsersTickets,
        ticketsCount,
        getSponsorDoc,
        isEmailProvider,
        isPhoneProvider,
        supportInfo,
        displayContentConfig,
        loading,
        usersCount: usersCount || 0,
        userIsAdvanced,
        sponsors,
        usertickets,
        points: userPoints,
        currentSponsor,
        selectedStoreType,
        setSelectedStoreType,
        getSelectedStore,
        userRoles,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

function getSponsorRef(
  location: LocationType,
  selectedStoreType?: { businessIds?: string[] } | undefined | null,
  pageLimit?: number,
) {
  const arr = getLocationQuery(location, false)

  let filterId: string[] | null = null
  if (selectedStoreType) {
    const ids = Array.from(new Set((selectedStoreType?.businessIds ?? []).filter(Boolean)))
    filterId = ids
  }
  //query(collection(db, `sponsors/${sponsorId}/services`), and(...arr))
  return {
    query: query(
      collection(firestore, "sponsors"),
      and(...arr),
      orderBy("order"),
      ...(pageLimit ? [limit(pageLimit)] : []),
    ),
    filterId,
  }
}

async function getDisplayContentConfig() {
  const querySnapshot = doc(firestore, "app-configurations/display-content-config")

  const ref = await getDoc(querySnapshot)
  const data: IDisplayContentConfig = ref.data() as any

  return data || {}
}
async function getSupportInfo() {
  const querySnapshot = doc(firestore, "app-configurations/technicalSupport")

  const ref = await getDoc(querySnapshot)
  const data: any = ref.data()

  return data
}
export function userTicketsQuery(id: string, raffleId?: string): Query<DocumentData, DocumentData> {
  const querySnapshot = collection(firestore, "users", id, "tickets")

  const queryRef = query(
    querySnapshot,

    raffleId
      ? and(
          where("expirationDate", ">=", Timestamp.now().toMillis()),
          where("disabled", "==", false),
          where("raffleId", "==", raffleId),
        )
      : and(
          where("expirationDate", ">=", Timestamp.now().toMillis()),
          where("disabled", "==", false),
        ),
  )

  return queryRef
}
